import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Send } from 'lucide-react';
import { getApiUrl, getConfig } from '../config';
import './RagAsk.css';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 180; // ~6 минут

function RagAsk() {
  const config = getConfig();
  const [question, setQuestion] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState(null);
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelRef.current = true;
    };
  }, []);

  const fetchResult = useCallback(async (jobId) => {
    const url = `${getApiUrl(config.api.endpoints.ragResult)}?job_id=${encodeURIComponent(jobId)}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Ошибка получения результата');
    }
    return data.data;
  }, [config]);

  const applyResult = useCallback((result) => {
    setAnswer(result.answer || '');
    setSources(Array.isArray(result.sources) ? result.sources : []);
    setStats(result.stats || null);
  }, []);

  const poll = useCallback(async (jobId) => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      if (cancelRef.current) return;
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (cancelRef.current) return;
      let result;
      try {
        result = await fetchResult(jobId);
      } catch (err) {
        continue; // транзиентная ошибка сети/прокси — продолжаем поллинг
      }
      if (result.status === 'completed') {
        applyResult(result);
        setStatusText('');
        setLoading(false);
        return;
      }
      if (result.status === 'failed') {
        setError(result.error || 'Не удалось сформировать ответ');
        setStatusText('');
        setLoading(false);
        return;
      }
      setStatusText('Анализирую комментарии…');
    }
    setError('Превышено время ожидания ответа. Попробуйте позже.');
    setLoading(false);
  }, [fetchResult, applyResult]);

  const handleAsk = useCallback(async () => {
    if (!question.trim()) {
      setError('Введите вопрос');
      return;
    }
    cancelRef.current = false;
    setLoading(true);
    setError(null);
    setAnswer('');
    setSources([]);
    setStats(null);
    setStatusText('Отправляю запрос…');

    try {
      const response = await fetch(getApiUrl(config.api.endpoints.ragAsk), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question.trim(), subject: subject.trim() }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Ошибка запроса');
      }
      const jobId = data.data.job_id;
      if (data.data.status === 'completed') {
        const result = await fetchResult(jobId);
        applyResult(result);
        setStatusText('');
        setLoading(false);
        return;
      }
      setStatusText('Анализирую комментарии…');
      poll(jobId);
    } catch (err) {
      setError(err.message);
      setStatusText('');
      setLoading(false);
    }
  }, [question, subject, config, poll, fetchResult, applyResult]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleAsk();
    }
  }, [handleAsk]);

  return (
    <div className="rag-ask">
      <div className="rag-ask-intro">
        <h2><Sparkles size={22} /> Вопрос к базе комментариев</h2>
        <p>
          Задайте вопрос на естественном языке (например, «За что недолюбливают пользователя chisomen?»).
          Система найдёт релевантные комментарии гибридным поиском (по смыслу и по ключевым словам) и
          сформирует ответ строго на основе найденных комментариев.
        </p>
      </div>

      <div className="rag-ask-form">
        <label className="rag-field">
          <span>Вопрос</span>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="За что ненавидят пользователя chisomen?"
            rows={3}
            disabled={loading}
          />
        </label>
        <label className="rag-field">
          <span>Субъект / ник (необязательно, можно несколько написаний через запятую)</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="chisomen, чистомен, чистоман"
            disabled={loading}
          />
        </label>
        <button type="button" className="rag-ask-btn" onClick={handleAsk} disabled={loading}>
          <Send size={18} />
          {loading ? 'Обработка…' : 'Сформировать ответ'}
        </button>
        <span className="rag-hint">Ctrl/Cmd + Enter — отправить</span>
      </div>

      {error && (
        <div className="error-message">
          <strong>Ошибка:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>{statusText || 'Обработка…'}</p>
          <p className="rag-wait-note">Ответ готовится на сервере, можно подождать — таймаута не будет.</p>
        </div>
      )}

      {!loading && answer && (
        <div className="rag-answer">
          <h3>Ответ</h3>
          <div className="rag-answer-text">{answer}</div>
          {stats && (
            <div className="rag-stats">
              Источники: ключевой поиск — {stats.keyword}, семантика — {stats.semantic},
              использовано — {stats.fused}
            </div>
          )}
        </div>
      )}

      {!loading && sources.length > 0 && (
        <div className="rag-sources">
          <h3>Использованные комментарии ({sources.length})</h3>
          <div className="rag-sources-list">
            {sources.map((s) => (
              <div key={s.comment_id} className="rag-source">
                <div className="rag-source-head">
                  <span className="rag-source-author">{s.author || '—'}</span>
                  <span className="rag-source-meta">
                    {s.datetime} · +{s.rating_plus}/-{s.rating_minus}
                    {typeof s.depth === 'number' && s.depth >= 0 ? ` · уровень ${s.depth}` : ''}
                  </span>
                  <span className="rag-badges">
                    {s.from_keyword && <span className="rag-badge kw">ключ</span>}
                    {s.from_semantic && <span className="rag-badge sem">смысл</span>}
                  </span>
                </div>
                <div className="rag-source-text">{s.text}</div>
                {s.parent_author && (
                  <div className="rag-source-parent">в ответ на @{s.parent_author}</div>
                )}
                {s.post_link && (
                  <a className="rag-source-link" href={s.post_link} target="_blank" rel="noreferrer">
                    Открыть пост
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default RagAsk;
